import { api } from '@/client/api';
import { useProcedure } from '@/client/useProcedure';
import { Section, SettingCard, SettingRow, ToneChip } from './kit';

/** Settings → Integrations.
 *
 *  The one screen in the product where ground rule 2 is visible to the trader rather than only to a reader of the
 *  repository. Every row is read from the running configuration — the binding each port is actually on, and whether
 *  it can actually be reached — so "is this real?" has an answer on screen instead of in a report.
 *
 *  Three states, and they are genuinely different:
 *
 *   · **Live** — a real provider, reachable.
 *   · **Simulated** — a labelled simulation. Deterministic, seeded, and badged everywhere its output appears.
 *   · **Dormant** — a real adapter with no credentials. Written and conformance-tested, never pointed at anything.
 *
 *  A dormant rail is the one worth being loud about: it is the state most easily mistaken for working.
 */
export function IntegrationsSection() {
  const { state, reload } = useProcedure(() => api.me.integrations.get(), []);

  return (
    <Section state={state} reload={reload}>
      {(data) => {
        const simulated = data.ports.filter((p) => p.simulated).length;
        const dormant = data.ports.filter((p) => !p.simulated && !p.ready).length;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <SettingCard
              title="Connected systems"
              sub={
                simulated === 0 && dormant === 0
                  ? 'Every port is bound to a live provider.'
                  : `${simulated} of ${data.ports.length} ports are simulated${dormant ? `, and ${dormant} live ${dormant === 1 ? 'adapter is' : 'adapters are'} dormant for want of credentials` : ''}. Anything they produce is labelled wherever it appears.`
              }
            >
              {data.ports.map((p) => (
                <SettingRow key={p.key} label={p.label} hint={p.detail}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px' }}>
                    <span
                      style={{
                        fontSize: '10.5px',
                        color: 'var(--faint)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {p.binding}
                    </span>
                    {p.simulated ? (
                      <ToneChip tone="warn">Simulated</ToneChip>
                    ) : p.ready ? (
                      <ToneChip tone="pos">Live</ToneChip>
                    ) : (
                      // Dormant, not broken. The adapter exists and is tested; it has simply never been given
                      // anything to talk to, and that distinction is the whole point of this row.
                      <ToneChip tone="mute">Dormant</ToneChip>
                    )}
                  </span>
                </SettingRow>
              ))}
            </SettingCard>

            <SettingCard
              title="What these words mean"
              sub="Stated here rather than in a tooltip, because the difference between them is the difference between a figure you can act on and one you cannot."
            >
              <SettingRow
                label="Live"
                hint="A real provider, reachable now. Figures from it are the real thing."
              />
              <SettingRow
                label="Simulated"
                hint="A deterministic stand-in, seeded so it tells the same story every time. Everything it produces carries a badge saying so."
              />
              <SettingRow
                label="Dormant"
                hint="A real adapter with no credentials configured. It is written and conformance-tested against a recorded transport, and it has never been pointed at the provider."
              />
            </SettingCard>
          </div>
        );
      }}
    </Section>
  );
}
